/**
 * Content for the /growing redesign landing page.
 *
 * Source: Figma node 8301-72948 (Wayflyer Website redesign).
 */

import { deployedAmount } from "@/lib/company-stats";

export const CTA_HREF = "https://app.wayflyer.com/authn/signup";
export const CTA_LABEL = "Get started";
export const APPLY_LABEL = "Apply now";

export const PAGE_SEO = {
  title: "Funding for Growing Brands | $200K-2M Working Capital | Wayflyer",
  description:
    "Purpose-built financing for brands with $100K-1M monthly revenue. Scale your brand effectively. Fast and hassle-free. Limited risk. No spend restrictions.",
};

export const HERO = {
  label: "Growing",
  title: "Unlock your brand\u2019s full potential.",
  description: "Our working capital helps ambitious brands scale effectively.",
};

export const CHALLENGES = {
  title:
    "You\u2019ve found a winning formula, but scaling brings new challenges...",
  cards: [
    {
      title: "Growth demands more capital.",
      description:
        "As your orders grow, more cash gets locked into inventory and marketing spend, stretching working capital further.",
    },
    {
      title: "Operations grow more complex",
      description:
        "Scaling requires ongoing investment in warehousing, logistics and team.",
    },
    {
      title: "Expansion needs liquid capital",
      description:
        "Expanding product lines and entering new markets requires cash, but it\u2019s tied up in the operating cycle.",
    },
    {
      title: "Traditional financing limits your growth",
      description:
        "Banks weren\u2019t built for growing consumer brands, leaving you stuck between constrained growth and your true potential.",
    },
  ],
} as const;

export const HOW_IT_WORKS = {
  title: "Apply in minutes. Access capital in hours.",
  steps: [
    {
      number: "01",
      title: "Tell us about your business.",
      description:
        "Once you\u2019ve created an account, take a moment to share some details about your business.",
    },
    {
      number: "02",
      title: "Securely connect your platforms.",
      description:
        "We analyse your performance using read-only data, then send you tailored financing offers.",
    },
    {
      number: "03",
      title: "Select an offer.",
      description:
        "Once approved, we can send the funds to your bank in as little as 24 hours.",
    },
  ],
} as const;

export const ELIGIBILITY = {
  title: "Are you eligible for financing?",
};

/** Figma 8301:73168 — criterion cards with inline titles in eligibility-mocks. */
export const ELIGIBILITY_CRITERIA = [
  { variant: "months" },
  { variant: "regions" },
  { variant: "revenue" },
  { variant: "industries" },
] as const;

export const VIDEOS = [
  {
    title: "Out of the rough",
    storyLabel: "Ghost Golf\u2019s story \u2192",
    category: "Inventory",
    description: "Ghost Golf\u2019s inventory mastery",
    playbackId: "Y004LOYCOAa7ZhB8YHX2o18fPKsxvVhOtZBpoKa02sDBQ",
    slug: "ghost-golf",
    storyHref: "/our-customers/ghost-golf",
  },
  {
    title: "Suds to success",
    storyLabel: "Spongell\u00e9\u2019s story \u2192",
    category: "OMNI-channel growth",
    description: "Spongell\u00e9 soaked up omnichannel growth",
    playbackId: "0102TD5yIkednGJRopnO00obj51XQoIj02cUV8Dc02OCoB00M",
    slug: "spongelle",
    storyHref: "/our-customers/spongelle",
  },
  {
    title: "Basement to boutiques",
    storyLabel: "Little Words Project\u2019s story \u2192",
    category: "Retail expansion",
    description: "Little Words Project\u2019s rise",
    playbackId: "x9RP47vs6uHqrDo24WnZ4IKTSQAcXTXC02uSrbK9eo028",
    slug: "little-words-project",
    storyHref: "/our-customers/little-words-project",
  },
  {
    title: "Style and substance",
    storyLabel: "True Classic\u2019s story \u2192",
    category: "OMNI-channel growth",
    description: "True Classic's astounding growth with Wayflyer",
    playbackId: "EC00dwAxMzhYtIeZEjYgAhNIz02MRt2mM2rzTTHLbiPL8",
    slug: "true-classic",
    storyHref: "/our-customers/true-classic",
  },
  {
    title: "Off the leash",
    storyLabel: "Petshop.co.uk\u2019s story \u2192",
    category: "OMNI-channel growth",
    description: "Petshop.co.uk unlocked operational excellence with Wayflyer",
    playbackId: "zvjyY01muqPpnT013018p6jtOUHL8Qwb1PihVc9ovseXZs",
    slug: "petshop-co-uk",
    storyHref: "/our-customers/petshop-co-uk",
  },
] as const;

export type GrowingVideo = (typeof VIDEOS)[number];

export function muxThumbnailUrl(playbackId: string) {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?fit_mode=smartcrop&time=0`;
}

export const TRANSPARENT_FEE = {
  title: "One transparent fee.\n\nNo hidden costs.",
  description:
    "Get reliable access to appropriate funding amounts at the right time. As your Amazon business grows, so does your available capital, with top-ups along the way.",
  image: "/assets-next/growing/transparent-fee/infographic.png",
};

export const VIDEO_SHOWCASE = {
  title: `We\u2019ve deployed ${deployedAmount("en")}+ to businesses worldwide`,
};

export const FOOTER_CTA = {
  title: "Get a financing\noffer today",
  primaryLabel: APPLY_LABEL,
  secondaryLabel: "Speak to us first",
  secondaryHref: "/contact-us",
};
